import Head from 'next/head';
import { useEffect, useState } from 'react';

import { getSEOConfig, structuredData } from '../utils/seo';
import { getMarketingNavData } from '../queries/marketingNav';

import Empty from '../layouts/empty';
import { ProgressBar } from '../components/landing/v2/ProgressBar';
import { Footer } from '../components/landing/v2/Footer';

import { LaunchHeader } from '../components/landing/v2/launch/LaunchHeader';
import { LaunchHero } from '../components/landing/v2/launch/LaunchHero';
import { ProblemSection } from '../components/landing/v2/ProblemSection';
import { SolutionOverviewSection } from '../components/landing/v2/SolutionOverviewSection';
import { CommerceSection } from '../components/landing/v2/CommerceSection';
import { VisualBuilderSection } from '../components/landing/v2/VisualBuilderSection';
import { ProductionFlowSection } from '../components/landing/v2/ProductionFlowSection';
import { FounderStorySection } from '../components/landing/v2/FounderStorySection';
import { MarketingQuoteSection } from '../components/website/MarketingQuoteSection';
import { DemoConversionSection } from '../components/landing/v2/DemoConversionSection';

// /launch — the launch announcement page, same family as /pricing, /demo and
// /roi-calculator: Empty layout, ISR, shared Footer. It does NOT use the global
// Navigation. Like /iwf-2026, it ships its own minimal chrome (LaunchHeader —
// logo + a single sign-up CTA) so a visitor arriving from Product Hunt or a
// launch post has one obvious next step instead of a mega-menu.
//
// Header and hero are bespoke. Everything below composes the shared landing/v2
// sections rather than restating them, so the launch page inherits copy and
// design-system fixes made anywhere else on the marketing site. Where /launch
// needs a section to render differently, that is expressed as a variant prop on
// the shared component whose default is the existing behavior — never a fork,
// and never a change that reaches /, /pricing or the platform pages.
//
// Indexable — getSEOConfig('launch') carries no `robots` override, so it emits
// 'index, follow' (dev/preview still auto-noindex); see src/utils/seo.js, and
// src/pages/api/sitemap.xml.js for the matching sitemap entry.
//
// Commercial scope is discussed in a written quote; this page publishes no price table.

export default function LaunchPage({ navData }) {
  // Marketing surface is pinned to dark mode (toggle removed from public
  // pages — see _app.js).
  const isDarkMode = true;
  const [scrollProgress, setScrollProgress] = useState(0);

  const seoConfig = getSEOConfig('launch');

  useEffect(() => {
    const handleScroll = () => {
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(maxScroll > 0 ? window.scrollY / maxScroll : 0);
    };
    // Passive: this listener never calls preventDefault, and a non-passive
    // scroll listener blocks the compositor.
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Head>
        <title>{seoConfig.title}</title>
        <meta name='title' content={seoConfig.title} />
        <meta name='description' content={seoConfig.description} />
        <meta name='keywords' content={seoConfig.keywords?.join(', ') || ''} />
        <meta name='author' content={seoConfig.author} />
        <meta name='robots' content={seoConfig.robots} />

        <link rel='canonical' href={seoConfig.canonical} />

        <meta property='og:type' content={seoConfig.ogType} />
        <meta property='og:url' content={seoConfig.ogUrl} />
        <meta property='og:title' content={seoConfig.ogTitle} />
        <meta property='og:description' content={seoConfig.ogDescription} />
        <meta property='og:image' content={seoConfig.ogImage} />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='630' />
        <meta property='og:site_name' content={seoConfig.siteName} />
        <meta property='og:locale' content='en_US' />

        <meta property='twitter:card' content='summary_large_image' />
        <meta property='twitter:url' content={seoConfig.ogUrl} />
        <meta property='twitter:title' content={seoConfig.ogTitle} />
        <meta
          property='twitter:description'
          content={seoConfig.ogDescription}
        />
        <meta property='twitter:image' content={seoConfig.ogImage} />

        <meta name='theme-color' content='#006B45' />

        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              structuredData.breadcrumbList([
                { name: 'Home', url: 'https://timbercloud.com' },
                { name: 'Launch', url: 'https://timbercloud.com/launch' },
              ]),
            ),
          }}
        />
      </Head>

      <LaunchHeader />

      <ProgressBar scrollProgress={scrollProgress} isDarkMode={isDarkMode} />

      <main className='min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 text-white'>
        {/* 1. The announcement itself — the only bespoke section. */}
        <LaunchHero />

        {/* 2. The problem: six tools, one order retyped at every hand-off. */}
        <ProblemSection isDarkMode={isDarkMode} />

        {/* 3. What replaces them — storefront, pricing, production. */}
        <div id='what-it-does'>
          <SolutionOverviewSection isDarkMode={isDarkMode} />
        </div>

        {/* 4. Sell: a catalog that can actually take an order. Compact media —
               on the homepage this mockup is the hero of its section; here it
               is one beat among nine, so it doesn't get to overscale. */}
        <CommerceSection mediaScale='compact' />

        {/* 5. Build: the 3D configurator that feeds the same geometry
               downstream to pricing and the cut list. */}
        <VisualBuilderSection isDarkMode={isDarkMode} />

        {/* 6. Ship: cut sheets, stations, inventory, fulfilment. */}
        <ProductionFlowSection />

        {/* 7. Founder story — lands as proof right before pricing, not as an
               interruption earlier in the argument. */}
        <FounderStorySection />

        {/* 8. Pricing, rendered from the shared section so it can't drift.
               Written-quote guidance and a demo CTA. The
               add-ons, services and Enterprise detail belong on the page a
               buyer navigates to deliberately. */}
        <MarketingQuoteSection />

        {/* 9. Final CTA. The IWF booth section is deliberately NOT reused
               here: IwfBooth's CTAs ("Get the code by email", the gated
               discount stub) funnel into IwfOfferForm, which only exists on
               /iwf-2026. The hero carries the show, booth and hall with a link
               through to that page instead. */}
        <DemoConversionSection />
      </main>

      <Footer isDarkMode={isDarkMode} navData={navData} />
    </>
  );
}

LaunchPage.getLayout = (page) => <Empty>{page}</Empty>;

// Opt out of _app.js's dashboard hydration shield — /launch must server-render
// its body + <Head> (incl. the robots tag) for crawlers and the link
// scrapers that unfurl a launch announcement.
LaunchPage.ssrEnabled = true;

export async function getStaticProps() {
  const navData = await getMarketingNavData();
  return { props: { navData }, revalidate: 300 };
}
